import {
  type Control,
  Controller,
  type FieldErrors,
  type UseFormRegister,
  type UseFormSetValue,
  useWatch,
} from 'react-hook-form'

import { Error } from '@/components/ui/Error'
import { FieldError } from '@/components/ui/field-error'
import { Input } from '@/components/ui/input'
import { SearchableSelect } from '@/components/ui/searchable-select'
import type { Equipment } from '../../../../models/equipment'
import { equipmentOption, PrefilledField } from './ClaimStep2Fields'
import { NOT_LISTED, type Step2FormValues } from './claimStep2Values'

const TRUCK_NOT_LISTED = { value: NOT_LISTED, label: 'Truck not on this list' }

/** Required for an unlisted truck; digits only, so a typo can't quietly save as no year. */
function validYear(value: string, form: Step2FormValues) {
  const year = value.trim()
  if (!year) return form.truckSelection !== NOT_LISTED || 'Year is required.'
  return /^\d{1,4}$/.test(year) || 'Enter the year in numbers, e.g. 2021.'
}

interface ClaimTruckSectionProps {
  control: Control<Step2FormValues>
  register: UseFormRegister<Step2FormValues>
  setValue: UseFormSetValue<Step2FormValues>
  errors: FieldErrors<Step2FormValues>
  trucks: Equipment[]
}

export function ClaimTruckSection({ control, register, setValue, errors, trucks }: ClaimTruckSectionProps) {
  const truckSelection = useWatch({ control, name: 'truckSelection' })
  const truckManual = truckSelection === NOT_LISTED
  const selectedTruck = trucks.find((t) => t.id === truckSelection)

  /** The picked unit's details, or empty boxes for a truck that is not on the list. */
  function seed(truck: Equipment | undefined) {
    setValue('truckVin', truck?.vin ?? '')
    setValue('truckPlate', truck?.licensePlate ?? '')
    setValue('truckYear', truck?.year != null ? String(truck.year) : '')
    setValue('truckMake', truck?.make ?? '')
    setValue('truckModel', truck?.model ?? '')
  }

  return (
    <>
      <Controller
        name="truckSelection"
        control={control}
        rules={{ required: 'Select the truck involved.' }}
        render={({ field, fieldState }) => (
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-neutral-700" htmlFor="claim-truck">
              Which truck was involved? *
            </label>
            <SearchableSelect
              id="claim-truck"
              options={trucks.map(equipmentOption)}
              value={field.value || undefined}
              onChange={(next) => {
                // On the pick itself, so the fleet list arriving never overwrites a saved claim.
                if (next !== field.value) seed(trucks.find((t) => t.id === next))
                field.onChange(next)
              }}
              footerOption={TRUCK_NOT_LISTED}
              placeholder="— Select truck —"
              searchPlaceholder="Search unit number, VIN or plate"
              emptyMessage="No truck matches that"
              aria-invalid={fieldState.error ? true : undefined}
            />
            <FieldError message={fieldState.error?.message} />
          </div>
        )}
      />

      {truckManual && (
        <>
          <Error small>
            <p className="font-semibold">Unlisted Truck — Coverage Not Guaranteed</p>
            <p className="mt-1 font-normal">
              This truck is not on your policy. Coverage for an unlisted vehicle is not guaranteed and your
              broker must verify before this claim can be submitted.
            </p>
          </Error>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-neutral-700">License plate</label>
              <Input placeholder="Leave blank if you don't have it" {...register('truckPlate')} />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-neutral-700">VIN</label>
              <Input placeholder="17-character VIN if available" {...register('truckVin')} />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-neutral-700">Year *</label>
              <Input
                placeholder="e.g. 2021"
                inputMode="numeric"
                aria-invalid={errors.truckYear ? true : undefined}
                {...register('truckYear', { validate: validYear })}
              />
              <FieldError message={errors.truckYear?.message} />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-neutral-700">Make *</label>
              <Input
                placeholder="e.g. Freightliner"
                aria-invalid={errors.truckMake ? true : undefined}
                {...register('truckMake', {
                  validate: (val, f) =>
                    f.truckSelection !== NOT_LISTED || val.trim() !== '' || 'Make is required.',
                })}
              />
              <FieldError message={errors.truckMake?.message} />
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-neutral-700">Model</label>
            <Input placeholder="e.g. Cascadia" {...register('truckModel')} />
          </div>
        </>
      )}

      {selectedTruck && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <PrefilledField label="VIN" {...register('truckVin')} />
          <PrefilledField
            label="License plate"
            placeholder="Add the plate if we do not have it"
            {...register('truckPlate')}
          />
          <div className="flex flex-col gap-1">
            <PrefilledField
              label="Year"
              inputMode="numeric"
              aria-invalid={errors.truckYear ? true : undefined}
              {...register('truckYear', { validate: validYear })}
            />
            <FieldError message={errors.truckYear?.message} />
          </div>
          <PrefilledField label="Make" {...register('truckMake')} />
          <div className="sm:col-span-2">
            <PrefilledField label="Model" {...register('truckModel')} />
          </div>
        </div>
      )}
    </>
  )
}
